'use client'
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";
import instance from "@/lib/axiosInstance";
import Image from "next/image";

// Authentication providers
const AUTH_PROVIDERS = [
    {
        id: "vatsim",
        name: "VATSIM",
        description: "Virtual Air Traffic Simulation Network",
        logo: "/images/vatsim-logo.svg",
        color: "bg-blue-600 hover:bg-blue-800",
        textColor: "text-white",
    },
    {
        id: "ivao",
        name: "IVAO",
        description: "International Virtual Aviation Organisation",
        logo: "/images/ivao-logo.svg",
        color: "bg-blue-800 hover:bg-blue-900",
        textColor: "text-white",
    },
];

// Component to handle search params with error handling
function Login() {
    const { user, isAuthenticated, isLoading: authLoading } = useAuth();
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authProvider, setAuthProvider] = useState<string | null>(null);
    const router = useRouter();

    // Redirect if already authenticated
    useEffect(() => {
        if (!authLoading && isAuthenticated && user) {
            const username = user.username;
            router.push(username ? `/${username}` : '/onboarding');
        }
    }, [authLoading, isAuthenticated, user, router]);

    // If authenticated, don't show login form (redirect will happen in useEffect)
    if (isAuthenticated && user) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                    <p className="text-sm text-muted-foreground">Redirecting...</p>
                </div>
            </div>
        );
    }

    const handleLogin = (provider: string) => {
        const route = new URLSearchParams(window.location.search).get('route') || '';
        setAuthProvider(provider);
        setIsAuthenticating(true);

        instance.get(`/auth/${provider}${route ? `?route=${encodeURIComponent(route)}` : ''}`)
            .then((res) => {
                const data = res.data;
                if (data.success) {
                    window.location.href = data.redirect_url;
                } else {
                    toast.error(`Failed to authenticate with ${provider.toUpperCase()}. Please try again.`);
                    router.push('/login?error=auth_failed');
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error(`Server error during ${provider.toUpperCase()} authentication. Please try again.`);
                router.push('/login?error=server_error');
            })
            .finally(() => {
                setIsAuthenticating(false);
            });
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-background/50 to-background p-4">
            <div className="w-full max-w-md space-y-8">
                {/* Logo and Branding */}
                <div className="flex flex-col items-center text-center">
                    <div className="h-16 w-16 bg-primary/10 flex items-center justify-center rounded-xl mb-4">
                        <Image
                            src="/images/dsp-logo.svg"
                            alt="FlightDeck Logo"
                            className="h-10 w-10"
                            width={40}
                            height={40}
                        />
                    </div>
                    <h1 className="text-3xl font-bold">Flight Deck</h1>
                    <p className="text-muted-foreground mt-2">Sign in to continue</p>
                </div>

                {/* Login Card */}
                <Card className="border shadow-md">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl text-center">Welcome back</CardTitle>
                        <CardDescription className="text-center">
                            Choose your flight simulation network to sign in
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Auth Provider Buttons */}
                        {AUTH_PROVIDERS.map((provider, index) => (
                            <React.Fragment key={provider.id}>
                                <Button
                                    variant="outline"
                                    size="lg"
                                    className={`cursor-pointer relative w-full flex justify-start gap-3 text-left font-normal p-6 ${provider.textColor
                                    } ${provider.color} border-none`}
                                    disabled={isAuthenticating}
                                    onClick={() => handleLogin(provider.id)}
                                >
                                    <div className="h-6 w-6 bg-white/90 rounded-sm flex items-center justify-center">
                                        <Image
                                            src={provider.logo}
                                            alt={`${provider.name} logo`}
                                            className="h-5 w-5"
                                            width={20}
                                            height={20}
                                        />
                                    </div>
                                    <div className={`flex flex-col ${provider.textColor}`}>
                                        <span className="font-semibold">Sign in with {provider.name}</span>
                                        <span className="text-xs opacity-90">{provider.description}</span>
                                    </div>
                                    {authProvider === provider.id && (
                                        <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                            <div className="h-5 w-5 border-2 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    )}
                                </Button>
                                {index < AUTH_PROVIDERS.length - 1 && (
                                    <div className="w-full relative">
                                        <Separator className="absolute inset-0 m-auto" />
                                        <div className="relative flex justify-center">
                                            <span className="bg-card px-2 text-xs text-muted-foreground">
                                                OR
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </CardContent>
                    <CardFooter className="flex flex-col gap-4">
                        <Button variant="ghost" size="sm">
                            Need Help?
                        </Button>
                    </CardFooter>
                </Card>

                {/* Footer Links */}
                <div className="flex justify-center space-x-4 text-sm text-muted-foreground">
                    <Link href="/privacy" className="hover:text-primary flex items-center gap-1">
                        Privacy Policy
                    </Link>
                    <span>•</span>
                    <Link href="/terms" className="hover:text-primary flex items-center gap-1">
                        Terms of Service
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default Login;